import { container } from "tsyringe";
import {
    type ContactData,
    type EmailComponent,
    type EmailTemplateData,
    type Message,
    type MessageAttachment,
    type OrderEmailTemplate,
} from "../api";
import { ValueObject } from "../base";
import { OrderEmailTemplateEntity } from "../documents";
import { MessageServiceToken, type MessageService } from "./MessageService";

/**
 * A message value represents an email message. A message contains
 * the sender, recipient(s), subject, HTML content, and optional attachments.
 *
 * @see Message
 */
export class MessageValue
    extends ValueObject(
        class {
            readonly from: string;
            readonly to: string;
            readonly cc?: string[];
            readonly subject: string;
            readonly html: string;
            readonly attachments?: MessageAttachment[];

            constructor(value: Message) {
                this.from = value.from;
                this.to = value.to;
                this.cc = value.cc;
                this.subject = value.subject;
                this.html = value.html;
                this.attachments = value.attachments;
            }
        }
    )
    implements Message
{
    async send(): Promise<void> {
        return await MessageValue.getService("send").sendMessage(this);
    }

    /**
     * Returns the sender email address.
     *
     * @returns The sender email address.
     */
    getFrom(): string {
        return this.from;
    }

    /**
     * Returns the recipient email address.
     *
     * @returns The recipient email address.
     */
    getTo(): string {
        return this.to;
    }

    /**
     * Returns the CC recipients if any.
     *
     * @returns The CC recipients array or undefined.
     */
    getCc(): string[] | undefined {
        return this.cc;
    }

    /**
     * Returns the message subject.
     *
     * @returns The message subject.
     */
    getSubject(): string {
        return this.subject;
    }

    /**
     * Returns the HTML content of the message.
     *
     * @returns The HTML content.
     */
    getHtml(): string {
        return this.html;
    }

    /**
     * Returns the message attachments if any.
     *
     * @returns The attachments array or undefined.
     */
    getAttachments(): MessageAttachment[] | undefined {
        return this.attachments;
    }

    /**
     * Checks if the message has CC recipients.
     *
     * @returns true if the message has CC recipients, false otherwise.
     */
    hasCc(): boolean {
        return Boolean(this.cc && this.cc.length > 0);
    }

    /**
     * Checks if the message has attachments.
     *
     * @returns true if the message has attachments, false otherwise.
     */
    hasAttachments(): boolean {
        return Boolean(this.attachments && this.attachments.length > 0);
    }

    /**
     * Gets the total number of recipients (to + cc).
     *
     * @returns The total number of recipients.
     */
    getTotalRecipientCount(): number {
        const ccCount = this.cc ? this.cc.length : 0;
        return 1 + ccCount; // 1 for 'to' recipient + CC count
    }

    /**
     * Gets all recipients (to + cc) as a flat array.
     *
     * @returns Array of all recipient email addresses.
     */
    getAllRecipients(): string[] {
        const recipients = [this.to];
        if (this.cc) {
            recipients.push(...this.cc);
        }
        return recipients;
    }

    /**
     * Checks if the message is valid (has required fields).
     *
     * @returns true if the message is valid, false otherwise.
     */
    isValid(): boolean {
        return Boolean(
            this.from &&
                this.from.trim() !== "" &&
                this.to &&
                this.to.trim() !== "" &&
                this.subject &&
                this.subject.trim() !== "" &&
                this.html &&
                this.html.trim() !== ""
        );
    }

    /**
     * Gets the message size estimation in characters.
     *
     * @returns Estimated size of the message content.
     */
    getEstimatedSize(): number {
        let size = this.from.length + this.to.length + this.subject.length + this.html.length;

        if (this.cc) {
            size += this.cc.reduce((total, email) => total + email.length, 0);
        }

        if (this.attachments) {
            size += this.attachments.reduce(
                (total, attachment) => total + attachment.filename.length + attachment.content.length,
                0
            );
        }

        return size;
    }

    /**
     * Creates a new message value with additional CC recipients.
     *
     * @param ccEmails The CC email addresses to add.
     * @returns A new MessageValue with the added CC recipients.
     */
    withCc(ccEmails: string[]): MessageValue {
        const existingCc = this.cc || [];
        const newCc = [...existingCc, ...ccEmails];

        return MessageValue.create({
            from: this.from,
            to: this.to,
            cc: newCc,
            subject: this.subject,
            html: this.html,
            attachments: this.attachments,
        });
    }

    /**
     * Creates a new message value with additional attachments.
     *
     * @param newAttachments The attachments to add.
     * @returns A new MessageValue with the added attachments.
     */
    withAttachments(newAttachments: MessageAttachment[]): MessageValue {
        const existingAttachments = this.attachments || [];
        const allAttachments = [...existingAttachments, ...newAttachments];

        return MessageValue.create({
            from: this.from,
            to: this.to,
            cc: this.cc,
            subject: this.subject,
            html: this.html,
            attachments: allAttachments,
        });
    }

    /**
     * Creates a new message value.
     *
     * @param value The message value to create.
     * @returns The created message value.
     * @throws An error if required fields are missing.
     */
    static create(value: Partial<Message>): MessageValue {
        if (!value.from || value.from.trim() === "") {
            throw new Error("Message: The 'from' field is required and cannot be empty.");
        }

        if (!value.to || value.to.trim() === "") {
            throw new Error("Message: The 'to' field is required and cannot be empty.");
        }

        if (!value.subject || value.subject.trim() === "") {
            throw new Error("Message: The 'subject' field is required and cannot be empty.");
        }

        if (!value.html || value.html.trim() === "") {
            throw new Error("Message: The 'html' field is required and cannot be empty.");
        }

        return new MessageValue({
            from: value.from,
            to: value.to,
            cc: value.cc,
            subject: value.subject,
            html: value.html,
            attachments: value.attachments,
        });
    }

    static async fromTemplate(
        templateData: EmailTemplateData,
        template: OrderEmailTemplate,
        renderFn: EmailComponent,
        subject: string
    ): Promise<MessageValue> {
        const templateEntity = OrderEmailTemplateEntity.create(template);
        const { attachments } = createEmailTemplate(subject, templateData, templateEntity);

        const provider = (await templateEntity.getServiceProvider())?.toExternalData();

        const salesContact = await templateEntity.getSalesContact();
        const customerContact = await templateEntity.getCustomerContact();

        return new MessageValue({
            ...templateData,
            from:
                provider.mailSender ??
                provider.salesContact?.email ??
                provider.company.primaryContact?.email ??
                provider.company.primaryContact?.email ??
                "",
            to: template.role === "sales" ? createRecipient(salesContact) : createRecipient(customerContact),
            subject,
            html: renderFn(await templateEntity.toExternalData()),
            attachments,
        });
    }

    private static getService(methodName: string): MessageService {
        if (!container.isRegistered(MessageServiceToken)) {
            throw new Error(`MessageValue: Cannot call "${methodName}" without a message service.`);
        }
        return container.resolve<MessageService>(MessageServiceToken);
    }
}

const createRecipient = (contact: ContactData): string => {
    const { firstName, lastName, email } = contact;
    return [firstName, lastName, `<${email}>`].filter(Boolean).join(" ");
};

const createEmailTemplate = (
    subject: string,
    templateData: EmailTemplateData,
    template: OrderEmailTemplateEntity
) => {
    const attachmentFileName = templateData.attachments?.find(
        attachment => attachment.id === template.documentTemplate
    )?.fileName;

    const attachments: MessageAttachment[] =
        attachmentFileName && template.document?.content
            ? [
                  {
                      filename: attachmentFileName,
                      content: template.document?.content,
                      encoding: "base64",
                  },
              ]
            : [];
    return { templateData, subject, attachments };
};
